/**
 * Role constants — the frontend half of the single role vocabulary.
 * Kept in lockstep with the backend source of truth
 * `backend/shared/src/qfly_shared/auth/roles.py` (parity is CI-locked by
 * `backend/shared/tests/test_roles.py::test_frontend_roles_ts_stays_in_sync`).
 * These must also match the Entra → Cognito group mapping documented in
 * docs/architecture/ARCHITECTURE.md §7 (Security).
 *
 * SA-2 target model (CEQ-SALESOPS-SPEC-001, confirmed 2026-07-24):
 * **Admin / Sales Rep / Basic User** — the three canonical roles that backend
 * capability enforcement keys on (see `qfly_shared.auth.capabilities`). They are
 * added here so the app recognises the values `/auth/me` returns once canonical
 * resolution is enabled.
 *
 * The remaining roles are LEGACY — retained for backward-compat (older tokens +
 * `custom_field_definitions.visibility.allowed_roles` still store them) until the
 * vocabulary is fully collapsed to the canonical three. No new code should gate
 * on the legacy roles.
 *
 * Do not hard-code role strings elsewhere — import the constant.
 */
export const Roles = {
  // Canonical (SA-2) — the enforced vocabulary.
  Admin: 'admin',
  SalesRep: 'sales_rep',
  BasicUser: 'basic_user',
  // Legacy — deprecated, kept for backward-compat only.
  SalesLeader: 'sales_leader',
  AccountManager: 'account_manager',
  Consultant: 'consultant',
  Finance: 'finance',
  ReadOnly: 'read_only',
} as const;

export type Role = (typeof Roles)[keyof typeof Roles];

/** The SA-2 canonical role set (Admin / Sales Rep / Basic User). */
export const CANONICAL_ROLES: readonly Role[] = [
  Roles.Admin,
  Roles.SalesRep,
  Roles.BasicUser,
];

export const isKnownRole = (value: string): value is Role =>
  (Object.values(Roles) as readonly string[]).includes(value);

/** True for the SA-2 canonical roles (excludes the legacy vocabulary). */
export const isCanonicalRole = (value: string): value is Role =>
  (CANONICAL_ROLES as readonly string[]).includes(value);
